<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PostSaleAgendaDayCarousel from './PostSaleAgendaDayCarousel.vue';

const props = defineProps({
  days: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  monthValue: { type: String, required: true },
  refreshLabel: { type: String, default: '' },
  selectedDate: { type: Date, required: true },
});

const emit = defineEmits([
  'nextMonth',
  'previousMonth',
  'refresh',
  'selectDate',
  'selectMonth',
  'today',
]);
const { t } = useI18n();
const monthPickerOpen = ref(false);

const monthLabel = computed(() => {
  const month = new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(
    props.selectedDate
  );
  const formattedMonth = month.replace(/^./, value => value.toUpperCase());

  return `${formattedMonth} ${props.selectedDate.getFullYear()}`;
});
const toggleMonthPicker = () => {
  monthPickerOpen.value = !monthPickerOpen.value;
};
const selectMonth = value => {
  monthPickerOpen.value = false;
  emit('selectMonth', value);
};
</script>

<template>
  <section
    class="min-w-0 w-full overflow-hidden border-y border-lais-border-soft bg-white"
  >
    <div
      class="flex min-w-0 flex-wrap items-center justify-between gap-3 px-5 py-2"
    >
      <div class="flex min-w-0 items-center gap-1">
        <LaisPrimaryButton variant="ghost" size="sm" @click="emit('today')">
          {{ t('POST_SALE.AGENDA.ACTIONS.TODAY') }}
        </LaisPrimaryButton>
        <button
          type="button"
          class="grid size-9 place-items-center rounded-full hover:bg-lais-primary-50"
          :aria-label="t('POST_SALE.AGENDA.ACTIONS.PREVIOUS_MONTH')"
          @click="emit('previousMonth')"
        >
          <span class="i-lucide-chevron-left size-4" aria-hidden="true" />
        </button>
        <div class="relative">
          <button
            type="button"
            class="inline-flex min-w-40 items-center justify-center gap-2 rounded-lais-sm px-3 py-2 text-sm font-semibold capitalize text-lais-text hover:bg-lais-primary-50"
            aria-haspopup="dialog"
            :aria-expanded="monthPickerOpen"
            aria-controls="post-sale-agenda-month-picker"
            @click="toggleMonthPicker"
          >
            <span class="i-lucide-calendar-days size-4" aria-hidden="true" />
            {{ monthLabel }}
          </button>
          <div
            v-if="monthPickerOpen"
            id="post-sale-agenda-month-picker"
            class="absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-lais border border-lais-border-soft bg-white p-4 text-left shadow-lais-card"
            role="dialog"
            @keydown.esc.stop="monthPickerOpen = false"
          >
            <label class="grid gap-2 text-xs font-semibold text-lais-text-body">
              {{ t('POST_SALE.AGENDA.MONTH_SELECTOR') }}
              <input
                :value="monthValue"
                type="month"
                class="h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-sm text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50"
                :aria-label="t('POST_SALE.AGENDA.MONTH_SELECTOR')"
                @change="selectMonth($event.target.value)"
              />
            </label>
          </div>
        </div>
        <button
          type="button"
          class="grid size-9 place-items-center rounded-full hover:bg-lais-primary-50"
          :aria-label="t('POST_SALE.AGENDA.ACTIONS.NEXT_MONTH')"
          @click="emit('nextMonth')"
        >
          <span class="i-lucide-chevron-right size-4" aria-hidden="true" />
        </button>
      </div>
      <div class="flex shrink-0 items-center gap-3">
        <LaisPrimaryButton
          variant="ghost"
          size="sm"
          icon="i-lucide-refresh-cw"
          :is-loading="loading"
          @click="emit('refresh')"
        >
          {{ t('POST_SALE.AGENDA.ACTIONS.REFRESH_PANEL') }}
        </LaisPrimaryButton>
        <span v-if="refreshLabel" class="text-xs text-lais-text-muted">
          {{ refreshLabel }}
        </span>
      </div>
    </div>

    <PostSaleAgendaDayCarousel
      :days="props.days"
      :selected-date="props.selectedDate"
      @select-date="emit('selectDate', $event)"
    />
  </section>
</template>
